/**
 * Insight: one typed chart and metric spec for dashboards, pages, slides, document templates and
 * record pages (plan §7.3, D11). This module holds the shape and the structural rules; the
 * model-aware validator, the compiler onto AggregateService and the ECharts renderer belong to
 * insight-engine.
 *
 * Owner: doc-engine (wave 1), dashboards-core (wave 2).
 */
import { z } from 'zod';
import { FilterNodeSchema, SortSpec, type FilterNode } from '../filter';
import { ApiKey } from '../ids';
import { LocalizedText } from '../localized-text';
import { ExprSource, FieldPath, type ExprSite } from './binding';

export const INSIGHT_VIZ = [
  'number',
  'bar',
  'column',
  'stacked',
  'line',
  'area',
  'donut',
  'funnel',
  'table',
  'pivot',
  'list',
  'progress',
  'gauge',
  'scatter',
] as const;
export const InsightViz = z.enum(INSIGHT_VIZ);
export type InsightViz = z.infer<typeof InsightViz>;

export const InsightBucket = z.enum(['day', 'week', 'month', 'quarter', 'year']);
export type InsightBucket = z.infer<typeof InsightBucket>;

const MeasureTime = z
  .object({
    field: FieldPath,
    window: z.union([z.enum(['context', 'none']), z.object({ from: z.string(), to: z.string() }).strict()]),
  })
  .strict();
const measureCommon = {
  label: LocalizedText.optional(),
  filter: FilterNodeSchema.optional(),
  time: MeasureTime.optional(),
};

export const InsightMeasure = z.discriminatedUnion('fn', [
  z.object({ fn: z.literal('count'), ...measureCommon }).strict(),
  z
    .object({ fn: z.enum(['sum', 'avg', 'min', 'max', 'median']), field: FieldPath, ...measureCommon })
    .strict(),
  z.object({ fn: z.literal('distinct'), field: FieldPath, ...measureCommon }).strict(),
  z
    .object({
      fn: z.literal('ratio'),
      /** Records matching `numerator` divided by records matching `denominator` (all when absent). */
      numerator: FilterNodeSchema,
      denominator: FilterNodeSchema.optional(),
      label: LocalizedText.optional(),
    })
    .strict(),
  z
    .object({
      fn: z.literal('expr'),
      source: ExprSource,
      aggregate: z.enum(['sum', 'avg', 'median', 'min', 'max']).optional(),
      ...measureCommon,
    })
    .strict(),
]);
export type InsightMeasure = z.infer<typeof InsightMeasure>;

export const InsightDimension = z
  .object({
    field: FieldPath,
    bucket: InsightBucket.optional(),
    top: z.number().int().min(1).max(100).optional(),
    other: z.boolean().optional(),
  })
  .strict();
export type InsightDimension = z.infer<typeof InsightDimension>;

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2})?)?$/, {
  message: 'Use an ISO date',
});

export const InsightTimeWindow = z.union([
  z.enum(['context', 'none']),
  z
    .object({
      last: z.number().int().min(1).max(1000),
      unit: z.enum(['day', 'week', 'month', 'quarter', 'year']),
      /**
       * Only whole periods that have ended: `{ last: 1, unit: 'quarter', completed: true }` is the last finished
       * quarter (a quarterly review reads the same figures all quarter long), not the one in progress.
       */
      completed: z.boolean().optional(),
    })
    .strict(),
  z.object({ from: IsoDate, to: IsoDate }).strict(),
]);

export const InsightFormat = z
  .object({
    kind: z.enum(['auto', 'number', 'money', 'percent', 'duration']),
    currency: z.union([z.literal('field'), z.string().regex(/^[A-Z]{3}$/)]).optional(),
    decimals: z.number().int().min(0).max(6).optional(),
    compact: z.boolean().optional(),
  })
  .strict();
export type InsightFormat = z.infer<typeof InsightFormat>;

export const InsightSource = z.union([
  z
    .object({ object: ApiKey, filter: FilterNodeSchema.optional(), relationPath: FieldPath.optional() })
    .strict(),
  z.object({ view: z.string().min(1).max(80) }).strict(),
]);

export const InsightShape = z
  .object({
    source: InsightSource,
    measures: z.array(InsightMeasure).min(1).max(4),
    dimensions: z.array(InsightDimension).max(2),
    time: z
      .object({
        field: FieldPath,
        window: InsightTimeWindow,
        compare: z.enum(['previous_period', 'previous_year']).optional(),
      })
      .strict()
      .optional(),
    viz: InsightViz,
    format: InsightFormat,
    goal: z
      .object({
        value: z.union([z.number().finite(), z.object({ expr: ExprSource }).strict()]),
        per: z.enum(['period', 'bucket']).optional(),
        baselineMonths: z.number().int().min(1).max(120).optional(),
        label: LocalizedText.optional(),
      })
      .strict()
      .optional(),
    goodWhen: z.enum(['up', 'down']).optional(),
    drill: z.union([
      z.enum(['records', 'none']),
      z.object({ dashboard: z.string().min(1).max(80), passFilters: z.literal(true) }).strict(),
    ]),
    /** A typed record list uses the same policy-aware query service and renderer. */
    records: z
      .object({
        valueField: z.string().min(1),
        secondaryField: z.string().optional(),
        sort: z.array(SortSpec).max(5),
        limit: z.number().int().min(1).max(100),
      })
      .strict()
      .optional(),
    /** Optional ordering of buckets (default: by the first measure, descending; time buckets ascending). */
    sort: z
      .object({ by: z.enum(['value', 'label']), dir: z.enum(['asc', 'desc']) })
      .strict()
      .optional(),
  })
  .strict();

export type Insight = {
  source: { object: string; filter?: FilterNode; relationPath?: FieldPath } | { view: string };
  measures: InsightMeasure[];
  dimensions: InsightDimension[];
  time?: {
    field: FieldPath;
    window:
      | 'context'
      | { last: number; unit: 'day' | 'week' | 'month' | 'quarter' | 'year'; completed?: boolean }
      | { from: string; to: string }
      | 'none';
    compare?: 'previous_period' | 'previous_year';
  };
  viz: InsightViz;
  format: InsightFormat;
  goal?: {
    value: number | { expr: string };
    per?: 'period' | 'bucket';
    baselineMonths?: number;
    label?: LocalizedText;
  };
  goodWhen?: 'up' | 'down';
  drill: 'records' | 'none' | { dashboard: string; passFilters: true };
  sort?: { by: 'value' | 'label'; dir: 'asc' | 'desc' };
  records?: { valueField: string; secondaryField?: string; sort: z.infer<typeof SortSpec>[]; limit: number };
};

/** Dimensions each visualisation accepts: [min, max]. */
const VIZ_DIMENSIONS: Record<InsightViz, [number, number]> = {
  number: [0, 0],
  progress: [0, 0],
  gauge: [0, 0],
  bar: [1, 2],
  column: [1, 2],
  stacked: [0, 2],
  line: [1, 2],
  area: [1, 2],
  donut: [1, 1],
  funnel: [1, 1],
  table: [0, 2],
  pivot: [2, 2],
  list: [1, 1],
  scatter: [0, 1],
};

/**
 * The structural rule an issue of `insightIssues` breaks, so callers can say it in the member's
 * language (`params` fill the message); `message` stays the English text.
 */
export type InsightRule =
  | 'records_list'
  | 'records_source'
  | 'groupings_none'
  | 'groupings_one'
  | 'groupings_exact'
  | 'groupings_range'
  | 'scatter_measures'
  | 'one_measure'
  | 'stacked'
  | 'goal'
  | 'compare_window'
  | 'currency'
  | 'other_top';

export type InsightStructureIssue = {
  path: (string | number)[];
  message: string;
  rule: InsightRule;
  params?: Record<string, string | number>;
};

/** Structural rules that need no data model (the model-aware rules live in insight-engine). */
export function insightIssues(insight: Insight): InsightStructureIssue[] {
  const issues: InsightStructureIssue[] = [];
  const [min, max] = VIZ_DIMENSIONS[insight.viz];
  const dims = insight.dimensions.length;
  const viz = insight.viz;
  if (insight.records && (viz !== 'list' || dims !== 1 || insight.dimensions[0]?.field.join('.') !== 'id'))
    issues.push({
      path: ['records'],
      rule: 'records_list',
      message: 'Record lists need the list visualization grouped by record ID',
    });
  if (insight.records && 'relationPath' in insight.source && insight.source.relationPath?.length)
    issues.push({
      path: ['records'],
      rule: 'records_source',
      message: 'Record lists need a direct object or saved view source',
    });
  if (dims < min || dims > max)
    issues.push(
      min === max
        ? {
            path: ['dimensions'],
            rule: min === 0 ? 'groupings_none' : min === 1 ? 'groupings_one' : 'groupings_exact',
            params: { viz, min },
            message: `A ${viz} chart needs ${min === 0 ? 'no grouping' : `${min} grouping${min > 1 ? 's' : ''}`}`,
          }
        : {
            path: ['dimensions'],
            rule: 'groupings_range',
            params: { viz, min, max },
            message: `A ${viz} chart needs ${min} to ${max} groupings`,
          },
    );
  if (viz === 'scatter' && insight.measures.length < 2)
    issues.push({ path: ['measures'], rule: 'scatter_measures', message: 'A scatter chart needs two measures' });
  if (['donut', 'funnel', 'number', 'progress', 'gauge'].includes(viz) && insight.measures.length > 1)
    issues.push({
      path: ['measures'],
      rule: 'one_measure',
      params: { viz },
      message: `A ${viz} chart shows one measure`,
    });
  if (viz === 'stacked' && dims < 2 && insight.measures.length < 2)
    issues.push({
      path: ['dimensions'],
      rule: 'stacked',
      message: 'A stacked chart needs a second grouping or a second measure',
    });
  if ((viz === 'progress' || viz === 'gauge') && !insight.goal)
    issues.push({ path: ['goal'], rule: 'goal', params: { viz }, message: `A ${viz} chart needs a goal` });
  if (insight.time?.compare && (!insight.time.window || insight.time.window === 'none'))
    issues.push({ path: ['time', 'compare'], rule: 'compare_window', message: 'Comparing needs a time window' });
  if (insight.format.currency === 'field' && insight.format.kind !== 'money')
    issues.push({ path: ['format', 'currency'], rule: 'currency', message: 'Only money values take a currency' });
  insight.dimensions.forEach((d, i) => {
    if (d.other && !d.top)
      issues.push({ path: ['dimensions', i, 'other'], rule: 'other_top', message: '"Other" needs a top limit' });
  });
  return issues;
}

export const Insight: z.ZodType<Insight> = InsightShape.superRefine((value, ctx) => {
  for (const issue of insightIssues(value as Insight))
    ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message });
}) as unknown as z.ZodType<Insight>;

/** Expression sites of an Insight (measure expressions and an expression goal). */
export function insightExprSites(insight: Insight): ExprSite[] {
  const sites: ExprSite[] = [];
  insight.measures.forEach((m, i) => {
    if (m.fn === 'expr')
      sites.push({
        path: ['measures', i, 'source'],
        source: m.source,
        role: 'expression',
        context: 'record',
        expected: { kind: 'number' },
      });
  });
  if (insight.goal && typeof insight.goal.value === 'object')
    sites.push({
      path: ['goal', 'value', 'expr'],
      source: insight.goal.value.expr,
      role: 'expression',
      context: 'record',
      expected: { kind: 'number' },
    });
  return sites;
}
